import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';

/**
 * ClientApplicationGaps Component
 * 
 * Identifies clients who may need attention:
 * 1. Clients with active cases but no university applications
 * 2. Stalled applications (draft status > 7 days)
 * 3. Overdue deadlines
 * 4. Incomplete checklists (< 50% complete)
 */
const ClientApplicationGaps = ({ onCreateApplication }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('no-apps');

    // Data states
    const [clientsWithoutApps, setClientsWithoutApps] = useState([]);
    const [stalledApps, setStalledApps] = useState([]);
    const [overdueApps, setOverdueApps] = useState([]);
    const [incompleteChecklists, setIncompleteChecklists] = useState([]);

    const load = async () => {
        try {
            setLoading(true);
            setError(null);

            // 1. Get clients with active cases but no university applications
            const { data: casesData } = await supabase
                .from('cases')
                .select(`
          id, title, status, created_at,
          clients (id, first_name, last_name, primary_email)
        `)
                .in('status', ['active', 'in_progress', 'pending'])
                .order('created_at', { ascending: false });

            const { data: allApps } = await supabase
                .from('uni_applications')
                .select('client_id');

            const clientsWithApps = new Set((allApps || []).map(a => a.client_id));

            const noAppsClients = (casesData || [])
                .filter(c => c.clients && !clientsWithApps.has(c.clients.id))
                .map(c => ({
                    clientId: c.clients.id,
                    clientName: `${c.clients.first_name || ''} ${c.clients.last_name || ''}`.trim() || 'Unknown',
                    email: c.clients.primary_email,
                    caseId: c.id,
                    caseTitle: c.title,
                    caseStatus: c.status,
                    caseCreated: c.created_at
                }));

            // Dedupe by client
            const seen = new Set();
            const uniqueNoApps = noAppsClients.filter(c => {
                if (seen.has(c.clientId)) return false;
                seen.add(c.clientId);
                return true;
            });
            setClientsWithoutApps(uniqueNoApps);

            // 2. Get stalled applications (draft > 7 days)
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

            const { data: stalledData } = await supabase
                .from('uni_applications')
                .select(`
          id, status, created_at, updated_at,
          clients (id, first_name, last_name),
          programs (name, university: universities (name))
        `)
                .eq('status', 'draft')
                .lt('updated_at', sevenDaysAgo.toISOString())
                .order('updated_at', { ascending: true });

            setStalledApps((stalledData || []).map(a => ({
                ...a,
                _clientName: `${a.clients?.first_name || ''} ${a.clients?.last_name || ''}`.trim() || 'Unknown',
                _university: a.programs?.university?.name || 'Unknown',
                _program: a.programs?.name || 'Unknown',
                _daysSinceUpdate: Math.floor((Date.now() - new Date(a.updated_at).getTime()) / (1000 * 60 * 60 * 24))
            })));

            // 3. Get overdue applications
            const now = new Date().toISOString();

            const { data: overdueData } = await supabase
                .from('uni_applications')
                .select(`
          id, status, metadata,
          clients (id, first_name, last_name),
          programs (name, university: universities (name)),
          intakes (term, apply_close)
        `)
                .not('status', 'in', '("accepted","rejected")')
                .order('updated_at', { ascending: false });

            const overdue = (overdueData || []).filter(a => {
                const deadline = a.intakes?.apply_close || a.metadata?.deadline;
                return deadline && new Date(deadline) < new Date();
            }).map(a => ({
                ...a,
                _clientName: `${a.clients?.first_name || ''} ${a.clients?.last_name || ''}`.trim() || 'Unknown',
                _university: a.programs?.university?.name || 'Unknown',
                _program: a.programs?.name || 'Unknown',
                _deadline: a.intakes?.apply_close || a.metadata?.deadline,
                _daysOverdue: Math.floor((Date.now() - new Date(a.intakes?.apply_close || a.metadata?.deadline).getTime()) / (1000 * 60 * 60 * 24))
            }));
            setOverdueApps(overdue);

            // 4. Get applications with incomplete checklists
            const { data: appsWithDocs } = await supabase
                .from('uni_applications')
                .select(`
          id, status,
          clients (id, first_name, last_name),
          programs (name, university: universities (name)),
          uni_app_documents (id, status)
        `)
                .not('status', 'in', '("accepted","rejected")');

            const incomplete = (appsWithDocs || [])
                .filter(a => {
                    const docs = a.uni_app_documents || [];
                    if (docs.length === 0) return false;
                    const completed = docs.filter(d => ['uploaded', 'submitted', 'approved'].includes(d.status)).length;
                    return (completed / docs.length) < 0.5;
                })
                .map(a => {
                    const docs = a.uni_app_documents || [];
                    const completed = docs.filter(d => ['uploaded', 'submitted', 'approved'].includes(d.status)).length;
                    return {
                        ...a,
                        _clientName: `${a.clients?.first_name || ''} ${a.clients?.last_name || ''}`.trim() || 'Unknown',
                        _university: a.programs?.university?.name || 'Unknown',
                        _program: a.programs?.name || 'Unknown',
                        _checklistTotal: docs.length,
                        _checklistCompleted: completed,
                        _checklistPercent: Math.round((completed / docs.length) * 100)
                    };
                });
            setIncompleteChecklists(incomplete);

        } catch (e) {
            console.error('Failed to load gap analysis:', e);
            setError('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const tabs = [
        { id: 'no-apps', label: 'No Applications', count: clientsWithoutApps.length, icon: '🚫', color: 'red' },
        { id: 'stalled', label: 'Stalled Apps', count: stalledApps.length, icon: '⏸️', color: 'yellow' },
        { id: 'overdue', label: 'Overdue', count: overdueApps.length, icon: '🔴', color: 'red' },
        { id: 'incomplete', label: 'Incomplete Docs', count: incompleteChecklists.length, icon: '📋', color: 'orange' }
    ];

    const totalAlerts = clientsWithoutApps.length + stalledApps.length + overdueApps.length + incompleteChecklists.length;

    if (loading) {
        return (
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                <div className="flex items-center justify-center gap-3">
                    <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
                    <span className="text-gray-400">Analyzing client gaps...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <span className="text-xl">🎯</span>
                    <div>
                        <h3 className="text-white font-bold">Client Application Gaps</h3>
                        <p className="text-xs text-gray-400">Clients needing attention • {totalAlerts} total alerts</p>
                    </div>
                </div>
                <button
                    onClick={load}
                    className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
                >
                    🔄 Refresh
                </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-700">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${activeTab === tab.id
                                ? 'bg-gray-700 text-white border-b-2 border-blue-500'
                                : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                            }`}
                    >
                        <span className="mr-1">{tab.icon}</span>
                        {tab.label}
                        {tab.count > 0 && (
                            <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] ${tab.color === 'red' ? 'bg-red-900/50 text-red-300' :
                                    tab.color === 'yellow' ? 'bg-yellow-900/50 text-yellow-300' :
                                        'bg-orange-900/50 text-orange-300'
                                }`}>
                                {tab.count}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div className="p-4 max-h-[400px] overflow-y-auto">
                {error && (
                    <div className="text-red-400 text-sm mb-4">{error}</div>
                )}

                {/* No Applications Tab */}
                {activeTab === 'no-apps' && (
                    <div className="space-y-3">
                        {clientsWithoutApps.length === 0 ? (
                            <div className="text-center text-gray-500 py-8">
                                ✅ All clients with active cases have applications
                            </div>
                        ) : (
                            clientsWithoutApps.map(c => (
                                <div
                                    key={c.clientId}
                                    className="bg-gray-900/50 border border-gray-700 rounded-lg p-3 flex items-center justify-between"
                                >
                                    <div>
                                        <div className="font-medium text-white">{c.clientName}</div>
                                        <div className="text-xs text-gray-400">
                                            Case: {c.caseTitle || c.caseId} • Status: {c.caseStatus}
                                        </div>
                                        {c.email && <div className="text-xs text-gray-500">{c.email}</div>}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Link
                                            to={`/admin/cases/${c.caseId}`}
                                            className="px-2 py-1 text-xs bg-gray-700 text-white rounded hover:bg-gray-600"
                                        >
                                            View Case
                                        </Link>
                                        <Link
                                            to={`/admin/univ_apps/new?client=${c.clientId}&case=${c.caseId}`}
                                            className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                                        >
                                            + Create App
                                        </Link>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* Stalled Apps Tab */}
                {activeTab === 'stalled' && (
                    <div className="space-y-3">
                        {stalledApps.length === 0 ? (
                            <div className="text-center text-gray-500 py-8">
                                ✅ No stalled applications
                            </div>
                        ) : (
                            stalledApps.map(a => (
                                <div
                                    key={a.id}
                                    className="bg-gray-900/50 border border-gray-700 rounded-lg p-3 flex items-center justify-between"
                                >
                                    <div>
                                        <div className="font-medium text-white">{a._clientName}</div>
                                        <div className="text-xs text-gray-400">
                                            {a._university} • {a._program}
                                        </div>
                                        <div className="text-xs text-yellow-400">
                                            Stalled for {a._daysSinceUpdate} days
                                        </div>
                                    </div>
                                    <Link
                                        to={`/admin/univ_apps/${a.id}`}
                                        className="px-2 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700"
                                    >
                                        Review
                                    </Link>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* Overdue Tab */}
                {activeTab === 'overdue' && (
                    <div className="space-y-3">
                        {overdueApps.length === 0 ? (
                            <div className="text-center text-gray-500 py-8">
                                ✅ No overdue applications
                            </div>
                        ) : (
                            overdueApps.map(a => (
                                <div
                                    key={a.id}
                                    className="bg-gray-900/50 border border-red-900/50 rounded-lg p-3 flex items-center justify-between"
                                >
                                    <div>
                                        <div className="font-medium text-white">{a._clientName}</div>
                                        <div className="text-xs text-gray-400">
                                            {a._university} • {a._program}
                                        </div>
                                        <div className="text-xs text-red-400">
                                            Deadline was {a._daysOverdue} days ago ({new Date(a._deadline).toLocaleDateString()})
                                        </div>
                                    </div>
                                    <Link
                                        to={`/admin/univ_apps/${a.id}`}
                                        className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                                    >
                                        Urgent
                                    </Link>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* Incomplete Checklists Tab */}
                {activeTab === 'incomplete' && (
                    <div className="space-y-3">
                        {incompleteChecklists.length === 0 ? (
                            <div className="text-center text-gray-500 py-8">
                                ✅ All applications have adequate documentation
                            </div>
                        ) : (
                            incompleteChecklists.map(a => (
                                <div
                                    key={a.id}
                                    className="bg-gray-900/50 border border-gray-700 rounded-lg p-3 flex items-center justify-between"
                                >
                                    <div className="flex-1">
                                        <div className="font-medium text-white">{a._clientName}</div>
                                        <div className="text-xs text-gray-400 mb-2">
                                            {a._university} • {a._program}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-24 h-2 bg-gray-700 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-orange-500"
                                                    style={{ width: `${a._checklistPercent}%` }}
                                                />
                                            </div>
                                            <span className="text-xs text-orange-400">
                                                {a._checklistCompleted}/{a._checklistTotal} docs ({a._checklistPercent}%)
                                            </span>
                                        </div>
                                    </div>
                                    <Link
                                        to={`/admin/univ_apps/${a.id}`}
                                        className="px-2 py-1 text-xs bg-orange-600 text-white rounded hover:bg-orange-700"
                                    >
                                        Complete
                                    </Link>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClientApplicationGaps;
